import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import ActionDropdown from '../../components/common/action-dropdown';
import InlineFilter from '../../components/ui/inline-filter';
import StatsCard from '../../components/common/StatsCard';
import {
  Users,
  TrendingUp,
  DollarSign,
  Award,
  Download,
  Filter,
} from 'lucide-react';
import { IB_ACCOUNT_FILTER_FIELDS } from '../../constants/filters';
import PageHeader from '../../components/common/PageHeader';
import { getStatusColor, capitalizeFirst } from '../../utils/helpers';
import CommissionTiers from '../../components/ib-accounts/CommissionTiers';
import QuickStats from '../../components/ib-accounts/QuickStats';
import NextTierProgress from '../../components/ib-accounts/NextTierProgress';
import ReferralLinksSharing from '../../components/ib-accounts/ReferralLinksSharing';
import RecentActivity from '../../components/ib-accounts/RecentActivity';

interface IBClient {
  id: string;
  name: string;
  email: string;
  registrationDate: string;
  status: 'active' | 'inactive' | 'pending';
  totalDeposits: number;
  totalTrades: number;
  commissionEarned: number;
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  lastActive: string;
}

interface CommissionTier {
  tier: string;
  minClients: number;
  minVolume: number;
  commissionRate: number;
  benefits: string[];
}

interface ReferralLink {
  id: string;
  name: string;
  url: string;
  clicks: number;
  signups: number;
  conversions: number;
  createdAt: string;
  isActive: boolean;
}

const IBAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showInlineFilters, setShowInlineFilters] = useState(false);

  const filterFields = IB_ACCOUNT_FILTER_FIELDS;

  const currentUserReferralCode = (() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      return storedUser?.ibReferralCode;
    } catch (error) {
      console.error('Error getting user from localStorage:', error);
      return undefined;
    }
  })();

  const mainReferralLink: ReferralLink = {
    id: 'ref-001',
    name: 'Your Referral Link',
    url: `${window.location.origin}/ref?code=${currentUserReferralCode}`,
    clicks: 590,
    signups: 54,
    conversions: 35,
    createdAt: '2024-01-15',
    isActive: true,
  };

  const ibClients: IBClient[] = [
    {
      id: '1',
      name: 'John Smith',
      email: 'john.smith@email.com',
      registrationDate: '2024-01-15',
      status: 'active',
      totalDeposits: 25000,
      totalTrades: 342,
      commissionEarned: 1250.5,
      tier: 'Gold',
      lastActive: '2 hours ago',
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      registrationDate: '2024-02-20',
      status: 'active',
      totalDeposits: 15000,
      totalTrades: 189,
      commissionEarned: 750.25,
      tier: 'Silver',
      lastActive: '1 day ago',
    },
    {
      id: '3',
      name: 'Mike Williams',
      email: 'mike.w@email.com',
      registrationDate: '2024-03-10',
      status: 'pending',
      totalDeposits: 5000,
      totalTrades: 45,
      commissionEarned: 125.0,
      tier: 'Bronze',
      lastActive: '3 days ago',
    },
    {
      id: '4',
      name: 'Emily Davis',
      email: 'emily.d@email.com',
      registrationDate: '2023-12-05',
      status: 'active',
      totalDeposits: 50000,
      totalTrades: 567,
      commissionEarned: 2850.75,
      tier: 'Platinum',
      lastActive: '5 hours ago',
    },
  ];

  const commissionTiers: CommissionTier[] = [
    {
      tier: 'Bronze',
      minClients: 0,
      minVolume: 0,
      commissionRate: 0.5,
      benefits: ['Basic commission', 'Monthly reports', 'Email support'],
    },
    {
      tier: 'Silver',
      minClients: 10,
      minVolume: 100000,
      commissionRate: 0.7,
      benefits: [
        'Enhanced commission',
        'Real-time tracking',
        'Priority support',
      ],
    },
    {
      tier: 'Gold',
      minClients: 25,
      minVolume: 500000,
      commissionRate: 1.0,
      benefits: [
        'Premium commission',
        'Advanced analytics',
        'Dedicated manager',
      ],
    },
    {
      tier: 'Platinum',
      minClients: 50,
      minVolume: 1000000,
      commissionRate: 1.5,
      benefits: [
        'Maximum commission',
        'Custom tools',
        'VIP support',
        'Revenue sharing',
      ],
    },
  ];

  const filteredIbClients = ibClients.filter(client => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        client.name.toLowerCase().includes(searchLower) ||
        client.email.toLowerCase().includes(searchLower) ||
        client.status.toLowerCase().includes(searchLower) ||
        client.tier.toLowerCase().includes(searchLower) ||
        client.lastActive.toLowerCase().includes(searchLower) ||
        client.totalDeposits.toString().includes(searchLower) ||
        client.totalTrades.toString().includes(searchLower) ||
        client.commissionEarned.toString().includes(searchLower) ||
        new Date(client.registrationDate)
          .toLocaleDateString()
          .toLowerCase()
          .includes(searchLower);

      if (!matchesSearch) return false;
    }

    if (activeFilters.status && client.status !== activeFilters.status) {
      return false;
    }

    if (activeFilters.tier && client.tier !== activeFilters.tier) {
      return false;
    }

    return true;
  });

  const totalCommission = filteredIbClients.reduce(
    (sum, client) => sum + client.commissionEarned,
    0
  );
  const activeClientsCount = filteredIbClients.filter(
    client => client.status === 'active'
  ).length;
  const totalVolume = filteredIbClients.reduce(
    (sum, client) => sum + client.totalDeposits,
    0
  );

  const currentTier = commissionTiers.find(t => t.tier === 'Gold');

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const columns: DataTableColumn<IBClient>[] = [
    {
      id: 'name',
      header: 'Client',
      accessor: row => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="text-sm text-gray-500">{row.email}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '150px',
    },
    {
      id: 'registrationDate',
      header: 'Registration Date',
      accessor: row => new Date(row.registrationDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.registrationDate,
      width: '150px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>
          {capitalizeFirst(row.status)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
    },
    {
      id: 'totalDeposits',
      header: 'Total Deposits',
      accessor: row => `$${row.totalDeposits.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalDeposits,
    },
    {
      id: 'totalTrades',
      header: 'Total Trades',
      accessor: row => row.totalTrades.toLocaleString(),
      sortable: true,
      sortValue: row => row.totalTrades,
    },
    {
      id: 'commissionEarned',
      header: 'Commission Earned',
      accessor: row => `$${row.commissionEarned.toFixed(2)}`,
      sortable: true,
      sortValue: row => row.commissionEarned,
      width: '150px',
    },
    {
      id: 'tier',
      header: 'Tier',
      accessor: row => (
        <Badge className={getStatusColor(row.tier, 'tier')}>
          {capitalizeFirst(row.tier)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.tier,
    },
    {
      id: 'lastActive',
      header: 'Last Active',
      accessor: row => row.lastActive,
      sortable: true,
      sortValue: row => row.lastActive,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View client:', row.id)}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Accounts"
          description="Manage your Introducing Broker clients and commissions"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Commission"
          value={`$${totalCommission.toFixed(2)}`}
          change="+12.5% from last month"
          icon={DollarSign}
        />

        <StatsCard
          title="Active Clients"
          value={activeClientsCount}
          change={`${ibClients.length} total clients`}
          icon={Users}
        />

        <StatsCard
          title="Total Volume"
          value={`$${totalVolume.toLocaleString()}`}
          change="Across all clients"
          icon={TrendingUp}
        />

        <StatsCard
          title="Current Tier"
          value="Gold"
          change="1.0% commission rate"
          icon={Award}
        />
      </div>

      <ReferralLinksSharing mainReferralLink={mainReferralLink} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CommissionTiers
            commissionTiers={commissionTiers}
            currentTier={currentTier}
          />
        </div>
        <div className="space-y-6">
          <QuickStats />
          <NextTierProgress />
          <RecentActivity />
        </div>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>IB Clients</CardTitle>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter IB Clients"
          />
          <DataTable
            columns={columns}
            data={filteredIbClients}
            searchValue={searchTerm}
            searchPlaceholder="Search by clients, reg. date, status, trades, commission, tier"
            onSearchChange={handleSearchChange}
            onSearchReset={handleSearchReset}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={setActiveFilters}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
            initialSortBy={{ id: 'registrationDate', desc: true }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IBAccounts;
